import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppTextService } from '@fi-shared/app-text';
import { FiAiChatHelperService } from '@fi-shared/fi-ai-chat/fi-ai-chat-helper.service';
import { FiAiChatService } from '@fi-shared/fi-ai-chat/fi-ai-chat.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ObjectTypes } from '@fi-shared/types';
import { getObjectTypeNameTextId } from '@fi-shared/utils/object-type-name';
import {
    AI_CHAT_ROLE_ASSISTANT,
    AI_CHAT_ROLE_USER,
    IAIChatSource,
    IAIConversationDetails,
    IAIConversationSummary,
    IAIConversationTurn,
} from '@fi-shared/fi-ai-chat/fi-ai-chat';

type PanelView = 'list' | 'conversation';

@Component({
    selector: 'fi-ai-chat-panel',
    templateUrl: './fi-ai-chat-panel.component.html',
    styleUrls: ['./fi-ai-chat-panel.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-ai-chat-panel' },
    standalone: false,
})
export class FiAiChatPanelComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Output() close = new EventEmitter<void>();
    @ViewChild('messagesScroll') messagesScrollRef: ElementRef<HTMLDivElement>;
    @ViewChild('promptInput') promptInputRef?: ElementRef<HTMLTextAreaElement>;

    protected readonly userRole = AI_CHAT_ROLE_USER;
    protected readonly assistantRole = AI_CHAT_ROLE_ASSISTANT;

    protected view: PanelView = 'list';
    protected conversations: IAIConversationSummary[] = [];
    protected isConversationsLoading = false;
    protected isConversationLoading = false;
    protected isSending = false;

    protected activeConversation: IAIConversationDetails | null = null;
    protected pendingMessages: IAIConversationTurn[] = [];
    protected prompt = '';

    // Translated through fiAppText in the template — the resolved text is what gets typed into the
    // prompt, so the user sends the question in their own application language.
    protected readonly suggestedPromptTextIds: string[] = [
        'ai.chat_suggested_prompt_1',
        'ai.chat_suggested_prompt_2',
        'ai.chat_suggested_prompt_3',
    ];

    private shouldScrollToBottom = false;
    private shouldFocusInput = false;

    constructor(
        private fiAiChatService: FiAiChatService,
        private fiAiChatHelperService: FiAiChatHelperService,
        private cdr: ChangeDetectorRef,
        private fiStateService: FiStateService,
        private appTextService: AppTextService,
        @Optional() private customerDataService: FiCustomerDataService | null,
    ) {}

    ngOnInit(): void {
        const restoredId = this.fiAiChatHelperService.activeConversationId;
        if (restoredId) {
            this.openConversation(restoredId);
        } else {
            this.loadConversations();
        }
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom) {
            this.shouldScrollToBottom = false;
            const el = this.messagesScrollRef?.nativeElement;
            if (el) el.scrollTop = el.scrollHeight;
        }
        if (this.shouldFocusInput) {
            this.shouldFocusInput = false;
            this.promptInputRef?.nativeElement.focus();
        }
    }

    protected onCloseClick(): void {
        this.close.emit();
    }

    protected onBackToListClick(): void {
        this.view = 'list';
        this.activeConversation = null;
        this.pendingMessages = [];
        this.fiAiChatHelperService.setActiveConversationId(null);
        this.loadConversations();
    }

    protected onNewChatClick(): void {
        this.view = 'conversation';
        this.activeConversation = null;
        this.pendingMessages = [];
        this.prompt = '';
        this.fiAiChatHelperService.setActiveConversationId(null);
        this.scrollToBottomLater();
        this.focusInputLater();
    }

    protected onConversationItemClick(conversation: IAIConversationSummary): void {
        this.openConversation(conversation.ConversationId);
    }

    protected onSuggestedPromptClick(text: string): void {
        this.prompt = text;
    }

    protected onSendClick(): void {
        const text = (this.prompt || '').trim();
        if (!text || this.isSending) return;

        const userTurn: IAIConversationTurn = { Role: AI_CHAT_ROLE_USER, Content: text, Sources: [] };
        this.pendingMessages = [...this.pendingMessages, userTurn];
        this.prompt = '';
        this.isSending = true;
        this.scrollToBottomLater();

        const conversationId = this.activeConversation?.ConversationId ?? this.fiAiChatHelperService.activeConversationId;
        const requestPromise = conversationId
            ? this.fiAiChatService.sendMessage({ Prompt: text, ConversationId: conversationId })
            : this.fiAiChatService.startConversation(text);

        requestPromise
            .then((response) => {
                const assistantTurn: IAIConversationTurn = {
                    Role: AI_CHAT_ROLE_ASSISTANT,
                    Content: response.Message,
                    Sources: response.Sources ?? [],
                    Preamble: response.Preamble,
                    Sections: response.Sections ?? [],
                    NoAnswer: response.NoAnswer,
                    FollowUps: response.FollowUps ?? [],
                };
                this.pendingMessages = [...this.pendingMessages, assistantTurn];
                this.fiAiChatHelperService.setActiveConversationId(response.ConversationId);

                if (this.activeConversation && this.activeConversation.ConversationId === response.ConversationId) {
                    this.activeConversation = {
                        ...this.activeConversation,
                        Messages: [...this.activeConversation.Messages, userTurn, assistantTurn],
                        UpdatedAt: new Date().toISOString(),
                    };
                    this.pendingMessages = [];
                } else if (!this.activeConversation && response.ConversationId) {
                    this.activeConversation = {
                        ConversationId: response.ConversationId,
                        Description: this.deriveDescription(text),
                        CreatedAt: new Date().toISOString(),
                        UpdatedAt: new Date().toISOString(),
                        Messages: [...this.pendingMessages],
                    };
                    this.pendingMessages = [];
                }
            })
            .catch(() => {
                this.pendingMessages = [
                    ...this.pendingMessages,
                    {
                        Role: AI_CHAT_ROLE_ASSISTANT,
                        Content: this.appTextService.get('ai.chat_error'),
                        Sources: [],
                    },
                ];
            })
            .then(() => {
                this.isSending = false;
                this.scrollToBottomLater();
                this.cdr.markForCheck();
            });
    }

    protected onPromptKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSendClick();
        }
    }

    // Follow-up suggestions for the latest answer. Shown above the input only while the input is
    // empty, nothing is sending, and the last message is an assistant answer that carries them —
    // so the block disappears the moment the user picks one or starts a new message.
    protected getActiveFollowUps(): string[] {
        if (this.isSending || (this.prompt || '').trim().length > 0) return [];
        const messages = this.getDisplayMessages();
        const last = messages[messages.length - 1];
        if (!last || !this.isAssistantTurn(last) || last.NoAnswer) return [];
        return last.FollowUps ?? [];
    }

    // Clicking a suggestion behaves exactly as if the user had typed it and pressed send.
    protected onFollowUpClick(question: string): void {
        if (this.isSending) return;
        this.prompt = question;
        this.onSendClick();
    }

    protected trackByFollowUp(_idx: number, q: string): string {
        return q;
    }

    protected getDisplayMessages(): IAIConversationTurn[] {
        const persisted = this.activeConversation?.Messages ?? [];
        return [...persisted, ...this.pendingMessages];
    }

    protected isAssistantTurn(turn: IAIConversationTurn): boolean {
        return turn.Role === AI_CHAT_ROLE_ASSISTANT;
    }

    protected hasSources(turn: IAIConversationTurn): boolean {
        return this.isAssistantTurn(turn) && !!turn.Sources?.length;
    }

    protected hasSections(turn: IAIConversationTurn): boolean {
        return this.isAssistantTurn(turn) && !!turn.Sections?.length;
    }

    protected onFeedbackClick(turn: IAIConversationTurn, index: number, rating: 'up' | 'down'): void {
        if (turn.Feedback === rating) return; // already this rating — nothing to toggle

        // Optimistic, non-blocking: reflect the (possibly toggled) rating immediately, fire-and-forget.
        turn.Feedback = rating;
        this.cdr.markForCheck();

        const messages = this.getDisplayMessages();
        const question = messages[index - 1]?.Content ?? '';

        this.fiAiChatService
            .saveFeedback({
                Rating: rating === 'up' ? 1 : 2,
                AnswerIndex: index, // position in the conversation → server updates in place on toggle
                Question: question,
                Answer: turn.Content,
                SourceDocumentIds: this.collectSourceIds(turn),
                ConversationId:
                    this.activeConversation?.ConversationId ??
                    this.fiAiChatHelperService.activeConversationId ??
                    undefined,
            })
            .catch(() => {
                // Best-effort signal — keep the rating shown even if the save failed.
            });
    }

    private collectSourceIds(turn: IAIConversationTurn): number[] {
        const ids = new Set<number>();
        (turn.Sources ?? []).forEach((s) => ids.add(s.ObjectId));
        (turn.Sections ?? []).forEach((sec) => (sec.Sources ?? []).forEach((s) => ids.add(s.ObjectId)));
        return Array.from(ids);
    }

    protected getSourceLabel(source: IAIChatSource): string {
        // Prefer the document title (resolved by CCM2); fall back to the clean path, then snippet.
        if (source.Title) return source.Title.trim();
        if (source.SourceUrl) return source.SourceUrl;
        if (source.Snippet) {
            const snippet = source.Snippet.trim();
            return snippet.length > 80 ? snippet.slice(0, 77) + '…' : snippet;
        }
        return this.appTextService.get('common.source');
    }

    // Icon + colour class per source ObjectType. The NAME is deliberately not defined here: it comes
    // from getObjectTypeNameTextId, the app-wide ObjectType → label-key mapper, so a source pill is
    // always labelled with the same (translated) module name the rest of the UI uses — e.g.
    // ObjectTypes.Document renders as "Pages", never as some AI-chat-only synonym.
    private static readonly SOURCE_TYPE_STYLES: { [objectType: number]: { icon: string; cls: string } } = {
        [ObjectTypes.Document]: { icon: 'fa-file-lines', cls: 'type-pages' },
        [ObjectTypes.Checklist2]: { icon: 'fa-square-check', cls: 'type-checklists' },
        [ObjectTypes.Form]: { icon: 'fa-clipboard-list', cls: 'type-forms' },
        [ObjectTypes.Post]: { icon: 'fa-bullhorn', cls: 'type-posts' },
        [ObjectTypes.FirstPage]: { icon: 'fa-house', cls: 'type-startpage' },
        [ObjectTypes.NewsFeed]: { icon: 'fa-newspaper', cls: 'type-news' },
    };

    protected getSourceType(source: IAIChatSource): { nameTextId: string; icon: string; cls: string } {
        const style = FiAiChatPanelComponent.SOURCE_TYPE_STYLES[source?.ObjectType];
        if (!style) return { nameTextId: 'common.source', icon: 'fa-file-lines', cls: 'type-default' };

        return { nameTextId: getObjectTypeNameTextId(source.ObjectType as ObjectTypes, true), ...style };
    }

    protected isRelativeUrl(url: string): boolean {
        return url.startsWith('/') && !url.startsWith('//');
    }

    protected navigateSource(event: MouseEvent, url: string): void {
        if (!this.isRelativeUrl(url)) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) return;
        event.preventDefault();
        const absoluteUrl = window.location.origin + url;
        const match = this.fiStateService.getStateByUrl(absoluteUrl);
        if (match) {
            this.fiStateService.go(match.stateName, match.stateParams);
        } else {
            window.location.href = url;
        }
    }

    protected trackBySummaryId(_idx: number, c: IAIConversationSummary): string {
        return c.ConversationId;
    }

    protected trackByTurnIndex(idx: number): number {
        return idx;
    }

    protected hasNoMessages(): boolean {
        return this.getDisplayMessages().length === 0;
    }

    // Customer support email for the "contact the support team" link shown on a no-answer reply.
    // Empty when the customer has no support contact configured (the link is then hidden).
    protected getSupportEmail(): string {
        return this.customerDataService?.getSupportEmailAddress() ?? '';
    }

    private openConversation(conversationId: string): void {
        this.view = 'conversation';
        this.isConversationLoading = true;
        this.activeConversation = null;
        this.pendingMessages = [];
        this.fiAiChatHelperService.setActiveConversationId(conversationId);

        this.fiAiChatService
            .getConversation(conversationId)
            .then((details) => {
                this.activeConversation = details;
            })
            .catch(() => {
                this.activeConversation = null;
            })
            .then(() => {
                this.isConversationLoading = false;
                this.scrollToBottomLater();
                this.cdr.markForCheck();
            });
    }

    private loadConversations(): void {
        this.isConversationsLoading = true;
        this.fiAiChatService
            .getConversations()
            .then((items) => {
                this.conversations = items ?? [];
            })
            .catch(() => {
                this.conversations = [];
            })
            .then(() => {
                this.isConversationsLoading = false;
                this.cdr.markForCheck();
            });
    }

    private scrollToBottomLater(): void {
        this.shouldScrollToBottom = true;
    }

    // Focus the prompt after the view renders so a new chat is immediately typable.
    private focusInputLater(): void {
        this.shouldFocusInput = true;
    }

    private deriveDescription(text: string): string {
        const trimmed = text.trim();
        return trimmed.length > 40 ? trimmed.slice(0, 37) + '…' : trimmed;
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
